/*

  Friend.ts

  Code for dialog components used in management of friends

  Copyright (C) 2025 Miðeind ehf.
  Original author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

export { FriendPromoteDialog };

import { m, ComponentFunc } from "./mithril";
import { IView } from "./types";
import { glyph, nbsp } from "./util";
import { mt, ts } from "./i18n";
import { logEvent } from "./channel";
import { MAX_FREE_NETSKRAFL } from "./model";

interface IAttributes {
  view: IView;
}

const FriendPromoteDialog: ComponentFunc<IAttributes> = (initialVnode) => {

  // A dialog that offers friendship (subscription) to the user

  const { view } = initialVnode.attrs;
  const { model } = view;

  return {
    view: () => {
      if (!model.state) return undefined;
      const state = model.state;

      return m(".modal-dialog",
        { id: "promo-dialog", style: { visibility: "visible" } },
        m(".ui-widget.ui-widget-content.ui-corner-all", { id: "promo-form" },
          [
            m(".promo-content",
              [
                m("h2",
                  [glyph("coffee-cup"), nbsp(), "Komdu í áskrift að Netskrafli!"]
                ),
                m("p",
                  "Með áskrift að Netskrafli færðu aðgang að öllu því sem leikurinn hefur að bjóða:"
                ),
                m("ul",
                  [
                    m("li", [
                      "Sem áskrifandi getur þú haft ",
                      m("b", "ótakmarkaðan fjölda viðureigna"),
                      " í gangi samtímis (í stað ",
                      m("b", MAX_FREE_NETSKRAFL.toString()),
                      " að hámarki)."
                    ]),
                    m("li", [
                      "Sem áskrifandi hefur þú aðgang að ",
                      m("b", "yfirliti"),
                      " í lok hverrar viðureignar þar sem hún er rakin leik fyrir leik ",
                      "og bestu möguleikar í hverri stöðu sýndir."
                    ]),
                    m("li", [
                      "Sem áskrifandi getur þú skorað á aðra leikmenn í ",
                      m("b", "keppnisham"),
                      ". Þá má leggja niður hvaða orð sem er, en andstæðingur getur véfengt þau — alveg eins ",
                      "og í borðskrafli og í keppni á skraflmótum. (BÍN er þó áfram endanlegur dómari um ",
                      "gild orð.)"
                    ]),
                    m("li", [
                      "Þú getur líka gerst áskrifandi að ",
                      m("b", "Málstað"),
                      ", máltæknivettvangi Miðeindar — og þá er Netskraflið innifalið! ",
                      "Á Málstað geturðu látið lesa yfir texta, þýtt á milli tungumála, ",
                      "breytt tali í texta, búið til skjátexta, fundargerðir og fleira."
                    ]),
                  ]
                ),
                m("p",
                  mt("strong", "Komdu með — og styrktu íslenska máltækni í leiðinni!")
                ),
              ]
            ),
            m(".btn-promo-yes",
              {
                id: "friend-yes",
                title: ts("Gerast áskrifandi"),
                tabindex: 1,
                onclick: (ev: Event) => {
                  ev.preventDefault();
                  logEvent("click_friend",
                    {
                      userid: state.netskraflUserId, locale: state.locale
                    }
                  );
                  // Navigate to the container-supplied subscription URL
                  window.location.href = state.subscriptionUrl;
                }
              },
              [glyph("ok"), nbsp(), ts("Gerast áskrifandi")]
            ),
            m(".btn-promo-no",
              {
                id: "friend-no",
                title: ts("Hætta við"),
                tabindex: 2,
                onclick: (ev: Event) => {
                  view.popDialog();
                  ev.preventDefault();
                }
              },
              [glyph("remove"), nbsp(), ts("Nei, takk")]
            ),
          ]
        )
      );
    }
  };
};
